import { Link as MuiLink, TableCell, TableRow } from "@mui/material";
import type { JSX } from "react";
import { Link } from "react-router-dom";

import { BulkCheckbox } from "../../../../components/bulk-selection/index.js";
import { StatusBadge } from "../../components/StatusBadge.js";
import type { AdminTicket } from "../../types.js";
import { slaBadge } from "./ticketListConsts.js";

/**
 * One admin ticket-list row — selection checkbox, ref, title link, status +
 * priority badges, requester / assignee, SLA pill, last-activity time.
 * Extracted from `TicketListPage.tsx`.
 */
export function TicketRow({
  ticket: t,
  isChecked,
  onSelect,
}: {
  ticket: AdminTicket;
  isChecked: boolean;
  onSelect: (checked: boolean, opts: { shiftKey: boolean }) => void;
}): JSX.Element {
  const sla = slaBadge(t.sla_status);
  return (
    <TableRow
      sx={{
        borderTop: 1,
        borderColor: "divider",
        transition: "background-color 150ms",
        bgcolor: isChecked ? "action.selected" : "transparent",
        "&:hover": isChecked ? undefined : { bgcolor: "action.hover" },
      }}
    >
      <TableCell>
        <BulkCheckbox
          checked={isChecked}
          onChange={(checked, opts) => onSelect(checked, { shiftKey: opts.shiftKey })}
          ariaLabel={`Select ticket ${t.key}`}
        />
      </TableCell>
      <TableCell sx={{ fontFamily: "ui-monospace, monospace", fontSize: 12, color: "text.disabled" }}>
        {t.key}
      </TableCell>
      <TableCell>
        <MuiLink
          component={Link}
          to={`/support/tickets/${t.id}`}
          color="text.primary"
          underline="hover"
          sx={{ fontWeight: 500 }}
        >
          {t.title}
        </MuiLink>
      </TableCell>
      <TableCell>
        <StatusBadge status={t.status} />
      </TableCell>
      <TableCell>
        <StatusBadge status={t.priority} />
      </TableCell>
      <TableCell sx={{ color: "text.disabled" }}>
        {t.requester_name || "—"}
      </TableCell>
      <TableCell sx={{ color: "text.disabled" }}>
        {t.assignee_name ?? "—"}
      </TableCell>
      <TableCell sx={{ fontSize: 12, fontWeight: 500, color: sla.color }}>
        {sla.label}
      </TableCell>
      <TableCell sx={{ color: "text.disabled" }}>
        {new Date(t.last_activity_at).toLocaleString()}
      </TableCell>
    </TableRow>
  );
}
